import { useSelector } from 'react-redux';
import { Text, Modal, Button, Navigation, Cell, Image, Divider, Spacer, DeprecatedIcon } from '@vinted/web-ui';
import useTranslate from 'hooks/useTranslate';
import useIsDarkMode from 'hooks/useIsDarkMode';
import useAbTest from 'hooks/useAbTest';
import ContentLoader from 'components/ContentLoader';
import ScrollableArea from 'components/ScrollableArea';
import { formatCurrency } from 'libs/utils/formatString';
import { getLocale } from 'state/intl/selectors';
import { getOrderPrepareUiState, getPricingUiState } from 'state/closet-promotion/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { UiState } from 'constants/ui';
import { AbTestVariant } from 'constants/abtest';
import { getAssetPath } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export let OrderReviewAction;

(function (OrderReviewAction) {
  OrderReviewAction[OrderReviewAction["PaymentSelection"] = 0] = "PaymentSelection";
  OrderReviewAction[OrderReviewAction["OrderConfirmation"] = 1] = "OrderConfirmation";
})(OrderReviewAction || (OrderReviewAction = {}));

const ClosetPromotionPreCheckoutModal = _ref => {
  let {
    pricing,
    show,
    onNextAction,
    onPreview,
    onBack,
    onDynamicPricingInfo
  } = _ref;
  const translate = useTranslate('closet_promotion.pre_checkout_modal');
  const isDarkMode = useIsDarkMode();
  const locale = useSelector(getLocale);
  const orderPrepareUiState = useSelector(getOrderPrepareUiState);
  const pricingUiState = useSelector(getPricingUiState);
  const isDynamicPricingFSEnabled = useSelector(getIsFeatureSwitchEnabled('web_closet_promotion_dynamic_pricing'));
  const dynamicPricingAbTest = useAbTest({
    abTestName: 'closet_promotion_dynamic_pricing',
    shouldTrackExpose: show && isDynamicPricingFSEnabled
  });
  const isDynamicPricingEnabled = isDynamicPricingFSEnabled && ((dynamicPricingAbTest === null || dynamicPricingAbTest === void 0 ? void 0 : dynamicPricingAbTest.variant) === AbTestVariant.A || (dynamicPricingAbTest === null || dynamicPricingAbTest === void 0 ? void 0 : dynamicPricingAbTest.variant) === AbTestVariant.B);

  function renderNavigation() {
    return /*#__PURE__*/_jsx(Navigation, {
      right: /*#__PURE__*/_jsx(Button, {
        deprecatedIconName: "x",
        theme: "amplified",
        styling: Button.Styling.Flat,
        testId: "closet-promotion-pre-checkout-back-button",
        onClick: onBack
      }),
      body: /*#__PURE__*/_jsx(Text, {
        type: Text.Type.Title,
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center,
        text: translate('title')
      })
    });
  }

  function renderConfirmationAction() {
    return /*#__PURE__*/_jsx("div", {
      className: "u-ui-margin-horizontal-large u-ui-margin-bottom-large",
      children: /*#__PURE__*/_jsx(Button, {
        text: translate('actions.proceed'),
        styling: Button.Styling.Filled,
        onClick: onNextAction,
        isLoading: orderPrepareUiState === UiState.Pending,
        testId: "closet-promotion-pre-checkout--proceed-button"
      })
    });
  }

  function renderIntro() {
    const values = {
      'learn-more': chunks => /*#__PURE__*/_jsx("span", {
        onClick: onPreview,
        children: /*#__PURE__*/_jsx(Text, {
          as: "a",
          clickable: true,
          text: chunks
        })
      })
    };
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsx(Text, {
          text: translate('intro.title'),
          type: Text.Type.Heading
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.visibility.title'),
        body: translate('intro.visibility.body'),
        prefix: /*#__PURE__*/_jsx(Image, {
          src: getAssetPath('avatars-group.svg', isDarkMode),
          size: Image.Size.Large
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.relevancy.title'),
        body: translate('intro.relevancy.body', values),
        prefix: /*#__PURE__*/_jsx(Image, {
          src: getAssetPath('hand-ok.svg', isDarkMode),
          size: Image.Size.Large
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.insights.title'),
        body: translate('intro.insights.body'),
        prefix: /*#__PURE__*/_jsx(Image, {
          src: getAssetPath('chart.svg', isDarkMode),
          size: Image.Size.Large
        })
      })]
    });
  }

  function renderPrice() {
    const totalPrice = formatCurrency(pricing.totalPrice.amount, {
      locale,
      currency: pricing.totalPrice.currencyCode
    });
    const discountedPrice = formatCurrency(pricing.discountedPrice.amount, {
      locale,
      currency: pricing.discountedPrice.currencyCode
    });
    const showTotalPrice = pricing.discountedPrice.amount < pricing.totalPrice.amount;
    const suffix = isDynamicPricingEnabled ? /*#__PURE__*/_jsx("div", {
      className: "u-flexbox",
      children: /*#__PURE__*/_jsx(DeprecatedIcon, {
        name: "info-circle",
        color: DeprecatedIcon.Color.GreyscaleLevel4,
        size: DeprecatedIcon.Size.Small
      })
    }) : null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Cell, {
        styling: Cell.Styling.Tight,
        suffix: suffix,
        onClick: isDynamicPricingEnabled ? onDynamicPricingInfo : undefined,
        clickable: isDynamicPricingEnabled,
        testId: "closet-promotion-dynamic-pricing-info",
        children: [showTotalPrice && /*#__PURE__*/_jsx(Text, {
          text: totalPrice,
          strikethrough: true
        }), /*#__PURE__*/_jsx(Spacer, {
          size: Spacer.Size.Small,
          orientation: Spacer.Orientation.Vertical
        }), /*#__PURE__*/_jsx(Text, {
          text: discountedPrice,
          type: Text.Type.Title
        })]
      }), pricing.isSalesTaxApplicable && /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Text, {
          theme: "muted",
          text: translate('sales_tax_note'),
          testId: "closet-promotion-pre-checkout-modal--sales-tax-note"
        })
      })]
    });
  }

  function renderDeal() {
    const pricePerDay = formatCurrency(pricing.pricePerDay.amount, {
      locale,
      currency: pricing.pricePerDay.currencyCode
    });
    return /*#__PURE__*/_jsx(Cell, {
      title: translate('duration_day_count', {
        count: pricing.effectiveDays
      }, {
        count: pricing.effectiveDays
      }),
      body: translate('price_per_day', {
        price: pricePerDay
      }),
      suffix: renderPrice()
    });
  }

  function renderContent() {
    if (pricingUiState === UiState.Pending) {
      return /*#__PURE__*/_jsx(ContentLoader, {
        testId: "pre-checkout-loader",
        styling: ContentLoader.Styling.Wide
      });
    }

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Image, {
        src: getAssetPath('closet-promotion-onboarding.png', isDarkMode)
      }), renderIntro(), /*#__PURE__*/_jsx(Divider, {}), renderDeal(), renderConfirmationAction()]
    });
  }

  return /*#__PURE__*/_jsx(Modal, {
    show: show,
    hasScrollableContent: true,
    testId: "closet-promotion-pre-checkout-modal",
    children: /*#__PURE__*/_jsx("div", {
      className: "u-fill-width",
      children: /*#__PURE__*/_jsxs(ScrollableArea, {
        children: [renderNavigation(), renderContent()]
      })
    })
  });
};

export default ClosetPromotionPreCheckoutModal;